import './SetPassword.css';
import UserService from '../Services/UserService';
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import LogoAnimated from '../Refs/LogoAminated.gif';

const SetPassword = () => {
    const { sessionId, userId } = useParams();
    const navigate = useNavigate();
    
    const [screenSize, setScreenSize] = useState('desktop');
    const [validToken, setValidToken] = useState(null);
    const [tokenError, setTokenError] = useState(null);
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [submissionSuccess, setSubmissionSuccess] = useState(false);
    const [submissionError, setSubmissionError] = useState(null);
    const [loading, setLoading] = useState(false);
    const [validationErrors, setValidationErrors] = useState([]);

    // Responsive screen size detection
    useEffect(() => {
        const handleResize = () => {
            const width = window.innerWidth;
            if (width >= 1024) {
                setScreenSize('desktop');
            } else if (width >= 768) {
                setScreenSize('tablet');
            } else {
                setScreenSize('mobile');
            }
        };

        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Validate token on mount
    useEffect(() => {
        if (!validToken && !tokenError) {
            validateLinkInformation();
        }
    }, []);

    const validateLinkInformation = async () => {
        try {
            const response = await UserService.checkSetPasswordToken(sessionId, userId);
            if (response.status >= 200 && response.status < 300) {
                setValidToken(true);
            } else {
                setTokenError('Invalid or expired token.');
            }
        } catch (error) {
            setTokenError('An error occurred while validating the token.');
        }
    };

    // Password validation
    const validatePassword = (password) => {
        const errors = [];
        if (password.length < 8) {
            errors.push('Password must be at least 8 characters long');
        }
        if (!/[A-Z]/.test(password)) {
            errors.push('Password must contain at least one uppercase letter');
        }
        if (!/[a-z]/.test(password)) {
            errors.push('Password must contain at least one lowercase letter');
        }
        if (!/[0-9]/.test(password)) {
            errors.push('Password must contain at least one number');
        }
        if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
            errors.push('Password must contain at least one special character');
        }
        return errors;
    };

    // Calculate password strength
    const getPasswordStrength = (password) => {
        let strength = 0;
        if (password.length >= 8) strength++;
        if (password.length >= 12) strength++;
        if (/[A-Z]/.test(password)) strength++;
        if (/[a-z]/.test(password)) strength++;
        if (/[0-9]/.test(password)) strength++;
        if (/[!@#$%^&*(),.?":{}|<>]/.test(password)) strength++;
        
        if (strength <= 2) return { label: 'Weak', color: '#f44336', width: '33%' };
        if (strength <= 4) return { label: 'Medium', color: '#ff9800', width: '66%' };
        return { label: 'Strong', color: '#4caf50', width: '100%' };
    };

    const handlePasswordChange = (e) => {
        const value = e.target.value;
        setNewPassword(value);
        if (value) {
            setValidationErrors(validatePassword(value));
        } else {
            setValidationErrors([]);
        }
    };

    const handlePasswordSubmit = async (e) => {
        e.preventDefault();
        setSubmissionError(null);

        // Validate passwords
        const errors = validatePassword(newPassword);
        if (errors.length > 0) {
            setSubmissionError('Please fix the password requirements');
            return;
        }

        if (newPassword !== confirmPassword) {
            setSubmissionError('Passwords do not match.');
            return;
        }

        setLoading(true);

        try {
            const response = await UserService.setNewPassword(sessionId, userId, newPassword);
            if (response.status >= 200 && response.status < 300) {
                setSubmissionSuccess(true);
                setTimeout(() => {
                    navigate('/console');
                }, 2000);
            } else {
                setSubmissionError('Failed to set new password.');
            }
        } catch (error) {
            setSubmissionError('An error occurred while setting the new password.');
        } finally {
            setLoading(false);
        }
    };

    const passwordStrength = newPassword ? getPasswordStrength(newPassword) : null;

    if (tokenError) {
        return (
            <div className={`setpassword-page setpassword-${screenSize}`}>
                <header className={`setpassword-header setpassword-header-${screenSize}`}>
                    <div className="header-content">
                        <div className="logo">
                            <img src={LogoAnimated} alt="Sofie AI Logo" className={`logo-animated-${screenSize}`} />
                            <div className='logo-text-container'>
                                <div className={`logo-text-line-1 logo-text-line-1-${screenSize}`}>Sofie</div>
                                <div className={`logo-text-line-2 logo-text-line-2-${screenSize}`}>AI Agent</div>
                            </div>
                        </div>
                    </div>
                </header>

                <div className={`setpassword-content setpassword-content-${screenSize}`}>
                    <div className={`setpassword-container setpassword-container-${screenSize}`}>
                        <div className="error-state">
                            <div className="error-icon">✗</div>
                            <h2 className={`step-title step-title-${screenSize}`}>Invalid Link</h2>
                            <p className={`step-description step-description-${screenSize}`}>
                                {tokenError}
                            </p>
                            <button
                                className={`btn-primary btn-primary-${screenSize}`}
                                onClick={() => navigate('/console')}
                            >
                                Go to Login
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (submissionSuccess) {
        return (
            <div className={`setpassword-page setpassword-${screenSize}`}>
                <header className={`setpassword-header setpassword-header-${screenSize}`}>
                    <div className="header-content">
                        <div className="logo">
                            <img src={LogoAnimated} alt="Sofie AI Logo" className={`logo-animated-${screenSize}`} />
                            <div className='logo-text-container'>
                                <div className={`logo-text-line-1 logo-text-line-1-${screenSize}`}>Sofie</div>
                                <div className={`logo-text-line-2 logo-text-line-2-${screenSize}`}>AI Agent</div>
                            </div>
                        </div>
                    </div>
                </header>

                <div className={`setpassword-content setpassword-content-${screenSize}`}>
                    <div className={`setpassword-container setpassword-container-${screenSize}`}>
                        <div className="success-state">
                            <div className="success-icon">✓</div>
                            <h2 className={`step-title step-title-${screenSize}`}>Password Set Successfully!</h2>
                            <p className={`step-description step-description-${screenSize}`}>
                                Your password has been updated. Redirecting you to login...
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (!validToken) {
        return (
            <div className={`setpassword-page setpassword-${screenSize}`}>
                <header className={`setpassword-header setpassword-header-${screenSize}`}>
                    <div className="header-content">
                        <div className="logo">
                            <img src={LogoAnimated} alt="Sofie AI Logo" className={`logo-animated-${screenSize}`} />
                            <div className='logo-text-container'>
                                <div className={`logo-text-line-1 logo-text-line-1-${screenSize}`}>Sofie</div>
                                <div className={`logo-text-line-2 logo-text-line-2-${screenSize}`}>AI Agent</div>
                            </div>
                        </div>
                    </div>
                </header>

                <div className={`setpassword-content setpassword-content-${screenSize}`}>
                    <div className={`setpassword-container setpassword-container-${screenSize}`}>
                        <div className="loading-state">
                            <div className="spinner"></div>
                            <p className={`step-description step-description-${screenSize}`}>
                                Validating your link...
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={`setpassword-page setpassword-${screenSize}`}>
            <header className={`setpassword-header setpassword-header-${screenSize}`}>
                <div className="header-content">
                    <div className="logo">
                        <img src={LogoAnimated} alt="Sofie AI Logo" className={`logo-animated-${screenSize}`} />
                        <div className='logo-text-container'>
                            <div className={`logo-text-line-1 logo-text-line-1-${screenSize}`}>Sofie</div>
                            <div className={`logo-text-line-2 logo-text-line-2-${screenSize}`}>AI Agent</div>
                        </div>
                    </div>
                </div>
            </header>

            <div className={`setpassword-content setpassword-content-${screenSize}`}>
                <div className={`setpassword-container setpassword-container-${screenSize}`}>
                    <h2 className={`step-title step-title-${screenSize}`}>Set Your Password</h2>
                    <p className={`step-description step-description-${screenSize}`}>
                        Please create a secure password for your account.
                    </p>

                    <form onSubmit={handlePasswordSubmit}>
                        <div className="form-group">
                            <label className="form-label">New Password *</label>
                            <div className="password-input-wrapper">
                                <input
                                    type={showPassword ? 'text' : 'password'}
                                    className="form-input"
                                    value={newPassword}
                                    onChange={handlePasswordChange}
                                    placeholder="Enter your password"
                                    required
                                />
                                <button
                                    type="button"
                                    className="password-toggle"
                                    onClick={() => setShowPassword(!showPassword)}
                                >
                                    {showPassword ? '👁️' : '👁️‍🗨️'}
                                </button>
                            </div>
                            
                            {newPassword && passwordStrength && (
                                <div className="password-strength">
                                    <div className="strength-bar">
                                        <div 
                                            className="strength-fill" 
                                            style={{
                                                width: passwordStrength.width,
                                                backgroundColor: passwordStrength.color
                                            }}
                                        ></div>
                                    </div>
                                    <span 
                                        className="strength-label" 
                                        style={{ color: passwordStrength.color }}
                                    >
                                        {passwordStrength.label}
                                    </span>
                                </div>
                            )}

                            {validationErrors.length > 0 && (
                                <div className="password-requirements">
                                    <p className="requirements-title">Password must contain:</p>
                                    <ul>
                                        {validationErrors.map((error, index) => (
                                            <li key={index} className="requirement-error">{error}</li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>

                        <div className="form-group">
                            <label className="form-label">Confirm Password *</label>
                            <div className="password-input-wrapper">
                                <input
                                    type={showConfirmPassword ? 'text' : 'password'}
                                    className={`form-input ${
                                        confirmPassword && newPassword !== confirmPassword ? 'invalid' : 
                                        confirmPassword && newPassword === confirmPassword ? 'valid' : ''
                                    }`}
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    placeholder="Confirm your password"
                                    required
                                />
                                <button
                                    type="button"
                                    className="password-toggle"
                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                >
                                    {showConfirmPassword ? '👁️' : '👁️‍🗨️'}
                                </button>
                            </div>
                            {confirmPassword && newPassword !== confirmPassword && (
                                <span className="error-message">Passwords do not match</span>
                            )}
                            {confirmPassword && newPassword === confirmPassword && (
                                <span className="success-message">✓ Passwords match</span>
                            )}
                        </div>

                        {submissionError && (
                            <div className="error-banner">{submissionError}</div>
                        )}

                        <button
                            type="submit"
                            className={`btn-primary btn-primary-${screenSize}`}
                            disabled={loading || validationErrors.length > 0 || newPassword !== confirmPassword || !newPassword}
                        >
                            {loading ? 'Setting Password...' : 'Set Password'}
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default SetPassword;